<template>
  <div class="admin-inspection-task-container">
    <el-row class="flex justify-start" :gutter="16">
      <el-col :span="4" class="room-floor-unit-tree">
        <community-inspection-tree ref="communityTree" @notifyQuery="handleTreeQuery"></community-inspection-tree>
      </el-col>
      <el-col :span="20" class="margin-left-sm">
        <!-- (1) PageHeader -->
        <ds-page-header :title="$t('adminInspectionTask.page.title')" :subtitle="$t('adminInspectionTask.page.subtitle')" />

        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="searchForm.startTime" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('adminInspectionTask.search.startTime')" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="searchForm.endTime" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('adminInspectionTask.search.endTime')" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="searchForm.state" clearable class="w-full"
                :placeholder="$t('adminInspectionTask.search.state')">
                <el-option v-for="item in stateTypes" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
              </el-select>
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              {{ $t('adminInspectionTask.search.button') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="getList">
            <template #left>
              <span class="ds-panel__title">{{ $t('adminInspectionTask.list.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('adminInspectionTask.page.empty')" @retry="getList">
            <el-table-column prop="taskId" :label="$t('adminInspectionTask.table.taskId')" align="center" width="150">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.taskId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="communityName" :label="$t('adminInspectionTask.table.communityName')" align="center"
              width="130" show-overflow-tooltip />
            <el-table-column prop="inspectionPlanName" :label="$t('adminInspectionTask.table.inspectionPlanName')"
              align="left" min-width="150" show-overflow-tooltip />
            <!-- 计划时间：起止两行 -->
            <el-table-column :label="$t('adminInspectionTask.table.planTime')" align="center" width="160">
              <template slot-scope="scope">
                <div class="ds-num">{{ scope.row.planInsTime || '-' }}</div>
                <div class="ds-num ds-cell-sub">{{ scope.row.planEndTime || '-' }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="actInsTime" :label="$t('adminInspectionTask.table.actInsTime')" align="center"
              width="160">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.actInsTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="originalPlanUserName"
              :label="$t('adminInspectionTask.table.originalPlanUserName')" align="center" width="110">
              <template slot-scope="scope">
                {{ scope.row.originalPlanUserName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="planUserName" :label="$t('adminInspectionTask.table.planUserName')" align="center"
              width="100" />
            <el-table-column prop="transferDesc" :label="$t('adminInspectionTask.table.transferDesc')" align="left"
              min-width="130" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.transferDesc || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="signTypeName" :label="$t('adminInspectionTask.table.signTypeName')" align="center"
              width="100" />
            <!-- 状态 Tag：20200408（已超时）/ 20200409（缺勤）用危险色，其余按文案语义推断 -->
            <el-table-column prop="stateName" :label="$t('adminInspectionTask.table.stateName')" align="center"
              width="120">
              <template slot-scope="scope">
                <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminInspectionTask.table.operation')" align="center" width="90"
              fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" type="text" @click="handleDetail(scope.row)">
                  {{ $t('adminInspectionTask.table.detail') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
            <template #summary>
              <span class="ds-tip-text">{{ $t('adminInspectionTask.list.tip') }}</span>
            </template>
          </ds-pagination-bar>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { listAdminInspectionTasks } from '@/api/inspection/adminInspectionTaskApi'
import CommunityInspectionTree from '@/components/inspection/communityInspectionTree'
import { getDict } from '@/api/community/communityApi'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'AdminInspectionTaskList',
  components: {
    CommunityInspectionTree,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        startTime: '',
        endTime: '',
        state: '',
        communityId: '',
        inspectionPlanId: '',
        staffId: '',
        orderByDesc: 'desc'
      },
      stateTypes: [],
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getDictData()
    this.getList()
  },
  methods: {
    async getDictData() {
      try {
        const response = await getDict('inspection_task', 'state')
        this.stateTypes = Array.isArray(response) ? response : []
      } catch (error) {
        console.error('Failed to get dict data:', error)
      }
    },
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size
        }
        const response = await listAdminInspectionTasks(params)

        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况。
        this.tableData = Array.isArray(response.inspectionTasks) ? response.inspectionTasks : []
        this.pagination.total = response.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('adminInspectionTask.page.loadFailed')
        console.error('Failed to get inspection tasks:', error)
      } finally {
        this.loading = false
      }
    },
    handleTreeQuery(params) {
      this.searchForm.communityId = params.communityId
      this.searchForm.inspectionPlanId = params.inspectionPlanId
      this.searchForm.staffId = params.staffId
      this.handleSearch()
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    /**
     * 重置筛选：仅清空可筛选字段，保留左侧树选中的条件。
     */
    handleReset() {
      this.searchForm.startTime = ''
      this.searchForm.endTime = ''
      this.searchForm.state = ''
      this.handleSearch()
    },
    handleDetail(row) {
      window.open(`/#/views/inspection/adminInspectionTaskDetail?taskId=${row.taskId}`)
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    /**
     * 状态语义类型：20200408「已超时」/ 20200409「缺勤」为危险色，其余按文案语义推断。
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'inspectionTask',
        text: row.stateName || ''
      }).type
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-inspection-task-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 表格次要文字 */
.ds-cell-sub {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

.margin-left-sm {
  margin-left: 10px;
}

.room-floor-unit-tree {
  padding-right: 0;
}
</style>
